<template>
  <Teleport to="body">
    <transition name="modal">
      <div v-if="open" class="modal-mask">
        <!-- Overlay -->
        <div class="fixed inset-0 bg-black/50" @click="$emit('close')"></div>
        <!-- Panel -->
        <div class="modal-wrapper">
          <div class="modal-container w-full max-w-2xl" @click.stop>
            <div
              class="bg-white px-3 sm:px-4 py-4 rounded-xl border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] flex flex-col max-h-[90vh]"
            >
              <!-- Header with close button -->
              <div class="flex items-center justify-end flex-shrink-0">
                <button
                  @click="$emit('close')"
                  class="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <svg
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Body - Scrollable -->
              <div class="modal-body px-4 overflow-y-auto flex-1">
                <div class="space-y-6">
                  <!-- Header -->
                  <div class="text-start">
                    <h1 class="text-2xl font-bold text-gray-900 mb-1">Reschedule Session</h1>
                    <p class="text-sm text-gray-500">
                      Please select a new date and time for your session.
                    </p>
                  </div>

                  <!-- Session Details -->
                  <h3 class="text-sm sm:text-md font-semibold text-gray-900 mb-2">Session Details</h3>

                  <div class="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                    <!-- Row 1: Tutor/Student and Subject -->
                    <div class="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-0">
                      <!-- Tutor/Student Section -->
                      <div class="flex-1 sm:pr-4">
                        <div class="mb-1 sm:mb-2">
                          <p class="text-xs text-gray-500 font-medium">{{ isTutorView ? 'Student' : 'Tutor' }}</p>
                        </div>
                        <div class="flex items-center gap-2">
                          <UserIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <p class="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                            {{ session?.tutorName }}
                          </p>
                        </div>
                      </div>

                      <!-- Vertical Divider - Hidden on mobile -->
                      <div class="hidden sm:block w-px h-12 bg-gray-200 mx-4"></div>

                      <!-- Subject Section -->
                      <div class="flex-1 sm:pl-4 min-w-0">
                        <div class="mb-1 sm:mb-2">
                          <p class="text-xs text-gray-500 font-medium">Subject</p>
                        </div>
                        <div class="flex items-center gap-2 min-w-0">
                          <BookOpenIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <p class="text-xs sm:text-sm font-semibold text-gray-900 truncate" :title="session?.subject">
                            {{ session?.subject }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Horizontal Divider -->
                    <div class="w-full h-px bg-gray-200 my-3"></div>

                    <!-- Row 2: Date and Time -->
                    <div class="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-0">
                      <!-- Date Section -->
                      <div class="flex-1 sm:pr-4">
                        <div class="mb-1 sm:mb-2">
                          <p class="text-xs text-gray-500 font-medium">Current Date</p>
                        </div>
                        <div class="flex items-center gap-2">
                          <CalendarIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <p class="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                            {{ formattedCurrentDate }}
                          </p>
                        </div>
                      </div>

                      <!-- Vertical Divider - Hidden on mobile -->
                      <div class="hidden sm:block w-px h-12 bg-gray-200 mx-4"></div>

                      <!-- Time Section -->
                      <div class="flex-1 sm:pl-4">
                        <div class="mb-1 sm:mb-2">
                          <p class="text-xs text-gray-500 font-medium">Current Time</p>
                        </div>
                        <div class="flex items-center gap-2">
                          <ClockIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <p class="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                            {{ session?.time }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Mode/Location Section -->
                    <div class="w-full h-px bg-gray-200 my-3"></div>
                    <div class="mb-1 sm:mb-2">
                      <p class="text-xs text-gray-500 font-medium mb-1">Mode & Location</p>
                    </div>
                    <div class="flex items-center gap-2">
                      <VideoCameraIcon
                        v-if="session?.mode === 'online'"
                        class="h-4 w-4 text-gray-400 flex-shrink-0"
                      />
                      <MapPinIcon v-else class="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div>
                        <p class="text-sm font-semibold text-gray-900">
                          <span v-if="session?.mode === 'face-to-face'" class="font-semibold">Face-to-face - {{ session?.location || "Location TBD" }}</span>
                          <span v-else-if="session?.mode === 'online'" class="font-semibold">Online Class</span>
                          <span v-else class="font-semibold">{{ session?.mode }} - {{ session?.location || "No location" }}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Calendar Card -->
                  <div class="rounded-3xl bg-white max-w-5xl mx-auto overflow-hidden">
                    <!-- Available Times Section -->
                    <div class="py-4">
                      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                        <div>
                          <h4 class="text-base sm:text-md font-semibold text-gray-900">Available Times</h4>
                          <p class="text-xs sm:text-sm text-gray-500">Timezone: Philippines (GMT+8)</p>
                          <div v-if="loadingError" class="mt-2">
                            <div class="text-xs sm:text-sm text-red-600 mb-2">{{ loadingError }}</div>
                            <button
                              @click="loadAvailableSlots"
                              class="px-3 py-1 text-xs sm:text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                            >
                              Retry Loading
                            </button>
                          </div>
                        </div>

                        <!-- Month Navigation -->
                        <div class="flex items-center justify-center sm:justify-start space-x-3 sm:space-x-4 sm:pr-4">
                          <button
                            @click="previousWeek"
                            class="arrow-button w-7 h-7 sm:w-6 sm:h-6 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                          >
                            <ChevronLeftIcon class="w-4 h-4 sm:w-3 sm:h-3" />
                          </button>

                          <span class="text-base sm:text-lg font-semibold text-gray-900 w-32 sm:w-40 text-center">{{
                            currentMonthYear
                          }}</span>

                          <button
                            @click="nextWeek"
                            :disabled="isNextWeekDisabled"
                            class="arrow-button w-7 h-7 sm:w-6 sm:h-6 flex items-center justify-center border rounded-md transition-colors"
                            :class="
                              isNextWeekDisabled
                                ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                                : 'border-gray-300 hover:bg-gray-100 cursor-pointer'
                            "
                          >
                            <ChevronRightIcon class="w-4 h-4 sm:w-3 sm:h-3" />
                          </button>
                        </div>
                      </div>

                      <!-- Mode Filtering Note -->
                      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                        <div class="flex items-start">
                          <InformationCircleIcon
                            class="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0"
                          />
                          <div class="ml-2">
                            <p class="text-sm text-blue-800">
                              Showing available slots for your chosen mode:
                              <span class="font-semibold capitalize">{{
                                originalSessionMode
                              }}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Calendar Grid -->
                    <div
                      class="bg-white border-2 mb-2 mx-2 border-black rounded-xl shadow-[6px_6px_0px_#000] overflow-hidden"
                    >
                      <!-- Desktop View: Column Layout (7 columns) -->
                      <div class="hidden sm:block">
                        <!-- Days Header with Dates -->
                        <div class="grid grid-cols-7 border-b-2 border-black">
                          <div
                            v-for="(day, index) in weekDays"
                            :key="`header-${day.date}-${day.month}`"
                            class="text-center py-4 px-2 border-r border-gray-200 last:border-r-0"
                            :class="{
                              'bg-blue-200 rounded-tl-xl': day.isToday && index === 0,
                              'bg-blue-200 rounded-tr-xl': day.isToday && index === 6,
                              'bg-blue-200': day.isToday && index !== 0 && index !== 6,
                              'bg-blue-100 rounded-tl-xl': !day.isToday && index === 0,
                              'bg-blue-100 rounded-tr-xl': !day.isToday && index === 6,
                              'bg-blue-100': !day.isToday && index !== 0 && index !== 6,
                            }"
                          >
                            <div class="text-xs font-medium text-gray-600 uppercase">
                              {{ dayHeaders[index] }}
                            </div>
                            <div
                              class="text-lg font-bold mt-1"
                              :class="{
                                'text-gray-400': day.isPast,
                                'text-gray-900': !day.isPast,
                              }"
                            >
                              {{ day.date }}
                            </div>
                          </div>
                        </div>

                        <!-- Time Slots Grid -->
                        <div class="grid grid-cols-7">
                          <div
                            v-for="(day, index) in weekDays"
                            :key="`slots-${day.date}-${day.month}`"
                            class="min-h-[180px] p-2 border-r border-gray-200 last:border-r-0 relative"
                            :class="{
                              'bg-gray-50 rounded-bl-3xl':
                                (day.isPast || day.timeSlots.length === 0) && index === 0,
                              'bg-gray-50 rounded-br-3xl':
                                (day.isPast || day.timeSlots.length === 0) && index === 6,
                              'bg-gray-50':
                                (day.isPast || day.timeSlots.length === 0) &&
                              index !== 0 &&
                              index !== 6,
                            'bg-white rounded-bl-3xl':
                              !day.isPast && day.timeSlots.length > 0 && index === 0,
                            'bg-white rounded-br-3xl':
                              !day.isPast && day.timeSlots.length > 0 && index === 6,
                            'bg-white':
                              !day.isPast && day.timeSlots.length > 0 && index !== 0 && index !== 6,
                          }"
                        >
                          <!-- Past Date or No Slots Message -->
                          <div
                            v-if="day.isPast"
                            class="flex flex-col items-center justify-center h-full text-gray-400"
                          >
                            <div class="text-xs font-medium">Past Date</div>
                          </div>

                          <div
                            v-else-if="day.timeSlots.length === 0"
                            class="flex flex-col items-center justify-center h-full text-gray-400"
                          >
                            <div class="text-xs text-center">No available slots</div>
                          </div>

                          <!-- Loading State -->
                          <div
                            v-else-if="isLoadingSlots"
                            class="flex flex-col items-center justify-center h-full text-gray-400"
                          >
                            <div class="animate-pulse">
                              <div class="space-y-2">
                                <div class="h-8 bg-gray-200 rounded w-full"></div>
                                <div class="h-8 bg-gray-200 rounded w-full"></div>
                                <div class="h-8 bg-gray-200 rounded w-full"></div>
                              </div>
                            </div>
                          </div>

                          <!-- Error State -->
                          <div
                            v-else-if="loadingError"
                            class="flex flex-col items-center justify-center h-full text-red-500"
                          >
                            <div class="text-center">
                              <div class="text-xs font-medium mb-1">Error Loading Slots</div>
                              <div class="text-xs mb-2">{{ loadingError }}</div>
                              <button
                                @click="loadAvailableSlots"
                                class="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                              >
                                Retry
                              </button>
                            </div>
                          </div>

                          <!-- Available Time Slots -->
                          <div v-else class="space-y-1.5 sm:space-y-2">
                            <button
                              v-for="slot in day.timeSlots"
                              :key="slot.time"
                              @click="selectTimeSlot(day, slot)"
                              class="w-full text-[10px] sm:text-xs py-2 sm:py-2 px-2 sm:px-1 rounded-md transition-all duration-200 font-medium whitespace-nowrap"
                              :class="getTimeSlotClass(day, slot)"
                              :disabled="
                                !slot.available || slot.isModeMismatch || slot.isCurrentSession
                              "
                              :title="getSlotTooltip(slot)"
                            >
                              {{ slot.time }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Mobile View: Row Layout (Each day is a row) -->
                    <div class="block sm:hidden space-y-3">
                      <div
                        v-for="(day, index) in weekDays"
                        :key="`mobile-${day.date}-${day.month}`"
                        class="bg-white border border-gray-300 rounded-lg overflow-hidden"
                        :class="{
                          'opacity-50': day.isPast || day.timeSlots.length === 0
                        }"
                      >
                        <!-- Day Header -->
                        <div 
                          class="py-3 px-4 border-b border-gray-300"
                          :class="{
                            'bg-blue-200': day.isToday,
                            'bg-blue-100': !day.isToday,
                          }"
                        >
                          <div class="flex items-center justify-between">
                            <div>
                              <div class="text-xs font-medium text-gray-600 uppercase">
                                {{ dayHeaders[index] }}
                              </div>
                              <div
                                class="text-xl font-bold mt-0.5"
                                :class="{
                                  'text-gray-400': day.isPast,
                                  'text-gray-900': !day.isPast,
                                }"
                              >
                                {{ day.date }}
                              </div>
                            </div>
                            <div v-if="day.isToday" class="text-xs font-semibold text-blue-600 bg-white px-2 py-1 rounded">
                              Today
                            </div>
                          </div>
                        </div>
                        
                        <!-- Time Slots -->
                        <div class="p-3">
                          <!-- Past Day -->
                          <div
                            v-if="day.isPast"
                            class="text-center py-4 text-gray-400 text-xs"
                          >
                            Past date
                          </div>
                          
                          <!-- No Slots -->
                          <div
                            v-else-if="day.timeSlots.length === 0"
                            class="text-center py-4 text-gray-400 text-xs"
                          >
                            No available slots
                          </div>
                          
                          <!-- Loading State -->
                          <div
                            v-else-if="isLoadingSlots"
                            class="flex justify-center items-center py-4"
                          >
                            <div class="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                          </div>
                          
                          <!-- Error State -->
                          <div
                            v-else-if="loadingError"
                            class="text-center py-4"
                          >
                            <div class="text-xs text-red-600 mb-2">{{ loadingError }}</div>
                            <button
                              @click="loadAvailableSlots"
                              class="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                            >
                              Retry
                            </button>
                          </div>
                          
                          <!-- Available Time Slots - Grid for mobile -->
                          <div v-else class="grid grid-cols-2 gap-2">
                            <button
                              v-for="slot in day.timeSlots"
                              :key="slot.time"
                              @click="selectTimeSlot(day, slot)"
                              class="text-xs py-2.5 px-2 rounded-md transition-all duration-200 font-medium"
                              :class="getTimeSlotClass(day, slot)"
                              :disabled="
                                !slot.available || slot.isModeMismatch || slot.isCurrentSession
                              "
                              :title="getSlotTooltip(slot)"
                            >
                              {{ slot.time }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>

                  <!-- Session Type Legend -->
                  <div class="flex justify-center">
                    <div class="flex items-center gap-6 text-xs text-gray-600 px-4 py-2 rounded-lg">
                      <div class="flex items-center gap-1">
                        <div class="w-3 h-3 rounded bg-blue-100 border border-blue-200"></div>
                        <span>Online</span>
                      </div>
                      <div class="flex items-center gap-1">
                        <div class="w-3 h-3 rounded bg-orange-100 border border-orange-200"></div>
                        <span>Face-to-Face</span>
                      </div>
                      <div class="flex items-center gap-1">
                        <div class="w-3 h-3 rounded bg-green-100 border border-green-200"></div>
                        <span>Both</span>
                      </div>
                    </div>
                  </div>

                  <!-- Selected Time Display -->
                  <div
                    v-if="selectedDateTime && selectedDateTime.date && selectedDateTime.timeSlot"
                    class="mt-6 p-4 bg-white border border-gray-200 rounded-lg"
                  >
                    <div class="flex items-start">
                      <!-- Date Section -->
                      <div class="flex-1 pr-4">
                        <div class="mb-2">
                          <p class="text-xs text-gray-500 font-medium">Selected Date</p>
                        </div>
                        <div class="flex items-center gap-2">
                          <svg
                            class="w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            ></path>
                          </svg>
                          <p class="text-sm font-semibold text-gray-900">
                            {{
                              new Date(
                                selectedDateTime.date.year,
                                selectedDateTime.date.month,
                                selectedDateTime.date.date
                              ).toLocaleDateString("en-US", {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            }}
                          </p>
                        </div>
                      </div>

                      <!-- Vertical Divider -->
                      <div class="w-px h-12 bg-gray-200 mx-4"></div>

                      <!-- Time Section -->
                      <div class="flex-1 pl-4">
                        <div class="mb-2">
                          <p class="text-xs text-gray-500 font-medium">Selected Time</p>
                        </div>
                        <div class="flex items-center gap-2">
                          <svg
                            class="w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            ></path>
                          </svg>
                          <p class="text-sm font-semibold text-gray-900">
                            {{
                              selectedDateTime.timeSlot.fullTimeRange ||
                              selectedDateTime.timeSlot.time
                            }}
                          </p>
                          <span
                            class="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full"
                          >
                            {{ selectedDateTime.timeSlot.durationText || "1h" }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Note -->
                  <div v-if="!isTutorView" class="bg-orange-50 border border-orange-200 p-3 rounded-lg pr-2">
                    <div class="flex items-start">
                      <InformationCircleIcon class="h-5 w-5 text-orange-700" />
                      <p class="text-sm text-orange-700 ml-3">
                        <strong>Note:</strong> Rescheduling requires your tutor's confirmation.
                      </p>
                    </div>
                  </div>

                  <!-- Footer buttons -->
                  <div class="flex justify-end space-x-3 pt-2 pr-2 mb-4">
                    <button
                      type="button"
                      @click="$emit('close')"
                      class="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      @click="handleReschedule"
                      :disabled="!canSubmit"
                      class="px-6 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span v-if="isSubmitting">Rescheduling...</span>
                      <span v-else>Reschedule Session</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import {
  BookOpenIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  VideoCameraIcon,
  InformationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/24/outline";
import { availabilityService } from "@/services/availabilityService";
import { sessionService } from "@/services/sessionService";
import { getStartOfWeek, addWeeksToDate, getWeekDays } from "@/utils/dateUtils";
import type { Session } from "@/types";

interface Props {
  open: boolean;
  session?: Session;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  reschedule: [data: { sessionId: string; newDate: string; newTime: string; reason?: string }];
}>();

// Lock body scroll when modal is open
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  } else {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  }
}, { immediate: true });

const reason = ref("");
const isSubmitting = ref(false);

// Calendar state
const currentDate = ref(new Date());
const selectedDateTime = ref<{ date: any; timeSlot: any } | null>(null);
const availableSlots = ref<Record<string, any[]>>({});
const isLoadingSlots = ref(true);
const loadingError = ref<string | null>(null);

const dayHeaders = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const tutorName = computed(() => props.session?.tutorName || "Tutor");

// Check if this is being used by a tutor (has studentId in session)
const isTutorView = computed(() => {
  return props.session?.studentId !== undefined;
});

// Get the original session mode for filtering
const originalSessionMode = computed(() => {
  return props.session?.mode?.toLowerCase() || "both";
});

// Filter slots to only show those matching the original session mode
const filterSlotsByMode = (slots: any[], dayDate: any) => {
  if (!slots || slots.length === 0) return [];

  return slots.map((slot) => {
    const slotMode = slot.mode?.toLowerCase();
    const isMatchingMode =
      slotMode === originalSessionMode.value ||
      slotMode === "both" ||
      originalSessionMode.value === "both";

    // Check if this is the current session slot (must match both date AND time)
    // Only check for current session if we're on the actual session date
    let isCurrentSession = false;
    if (props.session?.time && props.session?.date) {
      const sessionDate = new Date(props.session.date);
      const isSameDate =
        dayDate &&
        dayDate.date === sessionDate.getDate() &&
        dayDate.month === sessionDate.getMonth() &&
        dayDate.year === sessionDate.getFullYear();

      // Check if the slot starts at the same time as the session
      // Only compare start times, not full ranges, to avoid false matches
      const isSameTime =
        slot.time === props.session.time ||
        slot.startTimeFormatted === props.session.time ||
        slot.startTime === props.session.time;

      isCurrentSession = isSameDate && isSameTime;
    }

    return {
      ...slot,
      available: slot.available && isMatchingMode && !isCurrentSession,
      isModeMismatch: !isMatchingMode && slot.available && !isCurrentSession,
      isCurrentSession: isCurrentSession,
    };
  });
};

const currentMonthYear = computed(() => {
  const startOfWeek = getStartOfWeek(currentDate.value);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const startMonth = startOfWeek.toLocaleDateString("en-US", { month: "short" });
  const endMonth = endOfWeek.toLocaleDateString("en-US", { month: "short" });
  const year = startOfWeek.getFullYear();

  // If the week spans two different months, show "Sep - Oct 2024" format
  if (startOfWeek.getMonth() !== endOfWeek.getMonth()) {
    return `${startMonth} - ${endMonth} ${year}`;
  }

  // If the week is within the same month, show the month and year
  return currentDate.value.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
});

const formatSelectedDateTime = computed(() => {
  if (!selectedDateTime.value) return "";

  const { date, timeSlot } = selectedDateTime.value;
  const dateStr = new Date(date.year, date.month, date.date).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  // Show full details for availability slots
  if (timeSlot.isAvailabilitySlot) {
    return `${dateStr} from ${timeSlot.fullTimeRange}`;
  }

  return `${dateStr} at ${timeSlot.time}`;
});

const weekDays = computed(() => {
  const startOfWeek = getStartOfWeek(currentDate.value);
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Generate 7 days for the current week
  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    date.setHours(0, 0, 0, 0); // Reset time to compare only dates

    const dayKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    const rawSlots = availableSlots.value[dayKey] || [];

    const dayData = {
      date: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      isPast: date < today,
      isToday: date.getTime() === today.getTime(),
      timeSlots: [] as any[], // Will be set after filtering
    };

    // Filter slots with the day data for proper current session detection
    const filteredSlots = filterSlotsByMode(rawSlots, dayData);
    dayData.timeSlots = filteredSlots;

    days.push(dayData);
  }

  return days;
});

const getTimeSlotClass = (day: any, slot: any) => {
  const isSelected =
    selectedDateTime.value &&
    selectedDateTime.value.date.date === day.date &&
    selectedDateTime.value.date.month === day.month &&
    selectedDateTime.value.date.year === day.year &&
    selectedDateTime.value.timeSlot.time === slot.time;

  // Check if this is the current session slot (must match both date AND time)
  const isCurrentSession = slot.isCurrentSession;

  // Color code based on session mode
  const mode = slot.mode?.toLowerCase();

  if (isSelected) {
    // Selected state with color coding
    if (mode === "online") {
      return "bg-blue-600 text-white border border-blue-600";
    } else if (mode === "face-to-face" || mode === "in-person") {
      return "bg-orange-600 text-white border border-orange-600";
    } else if (mode === "both") {
      return "bg-green-600 text-white border border-green-600";
    }
    // Default selected state
    return "bg-green-600 text-white border border-green-600";
  }

  // Current session styling (looks like selected but disabled) - CHECK THIS FIRST
  if (isCurrentSession) {
    if (mode === "online") {
      return "bg-blue-600 text-white border border-blue-600 opacity-60 cursor-not-allowed";
    } else if (mode === "face-to-face" || mode === "in-person") {
      return "bg-orange-600 text-white border border-orange-600 opacity-60 cursor-not-allowed";
    } else if (mode === "both") {
      return "bg-green-600 text-white border border-green-600 opacity-60 cursor-not-allowed";
    }
    return "bg-green-600 text-white border border-green-600 opacity-60 cursor-not-allowed";
  }

  // Handle mode mismatch slots (disabled but visible)
  if (slot.isModeMismatch) {
    return "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200";
  }

  // Regular unavailable slots
  if (!slot.available) {
    return "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200";
  }

  // Unselected state with color coding
  if (mode === "online") {
    return "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors";
  } else if (mode === "face-to-face" || mode === "in-person") {
    return "bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 hover:border-orange-300 transition-colors";
  } else if (mode === "both") {
    return "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors";
  }

  // Default fallback - green for available slots
  return "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors";
};

const getSlotTooltip = (slot: any) => {
  if (slot.isModeMismatch) {
    return `Not available for ${originalSessionMode.value} mode - only ${slot.mode} slots available`;
  }

  if (slot.isCurrentSession) {
    return `Current session - ${slot.time} (${slot.mode})`;
  }

  if (slot.isAvailabilitySlot) {
    return `${slot.fullTimeRange} (${slot.durationText}, ${slot.mode})`;
  }

  return slot.time;
};

const selectTimeSlot = (day: any, slot: any) => {
  if (!slot.available || slot.isModeMismatch || slot.isCurrentSession) return;

  selectedDateTime.value = {
    date: day,
    timeSlot: slot,
  };
};

// Set current session as selected to show what's being rescheduled
const setCurrentSessionAsSelected = () => {
  if (!props.session?.date || !props.session?.time) return;

  try {
    // Parse the current session date
    const sessionDate = new Date(props.session.date);
    const sessionTime = props.session.time;

    // Find the matching day in the current week view
    const startOfWeek = getStartOfWeek(currentDate.value);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      date.setHours(0, 0, 0, 0);

      // Check if this day matches the session date
      if (
        date.getDate() === sessionDate.getDate() &&
        date.getMonth() === sessionDate.getMonth() &&
        date.getFullYear() === sessionDate.getFullYear()
      ) {
        // Find matching time slot
        const dayKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
        const slots = availableSlots.value[dayKey] || [];

        // Look for a slot that matches the session time
        const matchingSlot = slots.find((slot) => {
          // Check if the slot starts at the same time as the session
          const timeMatch =
            slot.time === sessionTime ||
            slot.startTimeFormatted === sessionTime ||
            slot.startTime === sessionTime;
          return timeMatch;
        });

        if (matchingSlot) {
          const dayData = {
            date: date.getDate(),
            month: date.getMonth(),
            year: date.getFullYear(),
            isPast: date < new Date(),
            isToday: date.getTime() === new Date().getTime(),
            timeSlots: slots,
          };

          // Don't auto-select the current session - just mark it for display
          // selectedDateTime.value = {
          //   date: dayData,
          //   timeSlot: matchingSlot
          // };

          return;
        } else {
        }
      }
    }
  } catch (error) {
    // Error handling for session selection
  }
};

const previousWeek = () => {
  const newDate = new Date(currentDate.value);
  newDate.setDate(newDate.getDate() - 7);
  currentDate.value = newDate;
  loadAvailableSlots();
};

const isNextWeekDisabled = computed(() => {
  return false; // Allow unlimited forward navigation
});

const nextWeek = () => {
  const newDate = new Date(currentDate.value);
  newDate.setDate(newDate.getDate() + 7);
  currentDate.value = newDate;
  loadAvailableSlots();
};

const loadAvailableSlots = async () => {
  isLoadingSlots.value = true;
  loadingError.value = null;

  try {
    // Load from tutor's Firebase availability
    if (props.session?.tutorId) {
      const startOfWeek = getStartOfWeek(currentDate.value);
      const slots = await availabilityService.generateWeeklySlots(
        props.session.tutorId,
        startOfWeek
      );
      availableSlots.value = slots;

      // Set current session as selected after loading slots
      setCurrentSessionAsSelected();
      return;
    }

    // Use default empty slots if no session data
    availableSlots.value = {};
  } catch (error) {
    loadingError.value = "Failed to load available time slots. Please try again.";
    // Fallback to empty slots on error
    availableSlots.value = {};
  } finally {
    isLoadingSlots.value = false;
  }
};

// Navigate to the week containing the session date
const navigateToSessionWeek = () => {
  if (!props.session?.date) return;

  try {
    const sessionDate = new Date(props.session.date);
    const sessionWeekStart = getStartOfWeek(sessionDate);

    // Only navigate if the session is not in the current week
    const currentWeekStart = getStartOfWeek(currentDate.value);
    if (sessionWeekStart.getTime() !== currentWeekStart.getTime()) {
      currentDate.value = sessionWeekStart;
      loadAvailableSlots();
    }
  } catch (error) {
    // Error handling for navigation
  }
};

// Format selected date for display
const formatSelectedDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Optimized date formatting with memoization
const formattedCurrentDate = computed(() => {
  if (!props.session?.date) return "";
  const date = new Date(props.session.date);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
});

// Reset form when modal opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      selectedDateTime.value = null;
      reason.value = "";

      // Navigate to the session's week first but don't auto-select
      navigateToSessionWeek();
    }
  },
  { immediate: true }
);

// Check if the selected date/time is different from the original session
const hasChangedDateTime = computed(() => {
  if (
    !selectedDateTime.value ||
    !selectedDateTime.value.date ||
    !selectedDateTime.value.timeSlot ||
    !props.session?.date ||
    !props.session?.time
  ) {
    return false;
  }

  try {
    // Parse the original session date
    const originalDate = new Date(props.session.date);
    const originalTime = props.session.time;

    // Get the selected date
    const selectedDate = new Date(
      selectedDateTime.value.date.year,
      selectedDateTime.value.date.month,
      selectedDateTime.value.date.date
    );

    // Get the selected time
    const selectedTime = selectedDateTime.value.timeSlot.time;

    // Compare dates (ignore time for date comparison)
    const datesMatch =
      originalDate.getDate() === selectedDate.getDate() &&
      originalDate.getMonth() === selectedDate.getMonth() &&
      originalDate.getFullYear() === selectedDate.getFullYear();

    // Compare times (handle different time formats)
    // Check if the selected slot starts at the same time as the original
    const timesMatch =
      selectedTime === originalTime ||
      selectedDateTime.value.timeSlot.startTimeFormatted === originalTime ||
      selectedDateTime.value.timeSlot.startTime === originalTime;

    // Return true if either date or time is different
    return !(datesMatch && timesMatch);
  } catch (error) {
    return false; // If there's an error, don't allow submission
  }
});

// Form validation
const canSubmit = computed(() =>
  Boolean(
    props.session?.id && selectedDateTime.value && !isSubmitting.value && hasChangedDateTime.value
  )
);

const handleReschedule = async () => {
  if (!canSubmit.value || !selectedDateTime.value) return;

  isSubmitting.value = true;

  try {
    // Convert time slot to time format for compatibility
    const timeSlot = selectedDateTime.value.timeSlot.time;
    const timeMatch = timeSlot.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    let formattedTime = timeSlot;

    if (timeMatch) {
      const [, hours, minutes, ampm] = timeMatch;
      let hour24 = parseInt(hours);
      if (ampm.toUpperCase() === "PM" && hour24 !== 12) {
        hour24 += 12;
      } else if (ampm.toUpperCase() === "AM" && hour24 === 12) {
        hour24 = 0;
      }
      formattedTime = `${hour24.toString().padStart(2, "0")}:${minutes}`;
    }

    // Format date for API
    const { date } = selectedDateTime.value;
    const formattedDate = `${date.year}-${(date.month + 1).toString().padStart(2, "0")}-${date.date
      .toString()
      .padStart(2, "0")}`;

    // Call the session service to reschedule with full time slot information
    await sessionService.rescheduleSession(
      props.session!.id,
      formattedDate,
      formattedTime,
      reason.value || undefined,
      selectedDateTime.value.timeSlot
    );

    // Emit success event
    emit("reschedule", {
      sessionId: props.session!.id,
      newDate: formattedDate,
      newTime: formattedTime,
      reason: reason.value || undefined,
    });

    // Close the modal
    emit("close");
  } catch (error) {
    alert("Failed to reschedule session. Please try again.");
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  loadAvailableSlots();
});
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  will-change: opacity;
}

.modal-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
}

.modal-container {
  margin: 0 auto;
  position: relative;
  will-change: transform, opacity;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.modal-enter-active {
  transition: opacity 250ms ease-out;
}

.modal-leave-active {
  transition: opacity 200ms ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.arrow-button {
  border: 2px solid #000000;
  border-radius: 20%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 2px 0px #000000;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
